import type { ErrorComponentProps } from "@tanstack/react-router";
import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet, Link, createRootRouteWithContext, useRouter, HeadContent, Scripts,
} from "@tanstack/react-router";
import appCss from "../styles.css?url";
import interLatin from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import spaceGroteskLatin from "@fontsource-variable/space-grotesk/files/space-grotesk-latin-wght-normal.woff2?url";
import { AuthProvider } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { BrandingProvider } from "@/lib/branding";
import { CookieConsentProvider } from "@/lib/cookie-consent";
import { UpdateAvailableBanner } from "@/components/UpdateAvailableBanner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold">We couldn't find that page</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          It may have moved. Here's where most people are headed:
        </p>
        <Link
          to="/signup"
          className="mt-6 inline-flex rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Start free — create your organization
        </Link>
        <div className="mt-6 grid gap-2 text-sm sm:grid-cols-2">
          {[
            { to: "/" as const, label: "Home" },
            { to: "/about" as const, label: "About MissionReady" },
            { to: "/pricing" as const, label: "Pricing" },
            { to: "/lead-magnet/security-ops-checklist" as const, label: "Free ops checklist" },
            { to: "/contact" as const, label: "Contact us" },
            { to: "/login" as const, label: "Sign in" },
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="rounded-md border border-border px-3 py-2 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}


function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
        <div className="mt-6 flex justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Try again
          </button>
          <a href="/" className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent">
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, maximum-scale=5, viewport-fit=cover" },
      { title: "Mission Ready — Security operations & field ops platform" },
      { name: "description", content: "All-in-one security operations platform for guard, patrol, and field teams: dispatch, GPS, incidents, checkpoints, scheduling, billing, and reporting." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "MissionReady" },
      { name: "theme-color", content: "#080A0C" },
      { name: "color-scheme", content: "dark" },
      { name: "format-detection", content: "telephone=no" },
      // iOS / mobile PWA
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "MissionReady" },
      { name: "application-name", content: "MissionReady" },
      { name: "google-site-verification", content: "7fWxu0nt0vvwHsD2gX6bFPVFc_BmkSQ5oq1ts3Kht2I" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/icon-192.png" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      // Fonts are self-hosted (see src/styles.css) and served from our own
      // origin, so there is no third-party preconnect. Preload only the two
      // latin variable files that render above-the-fold text.
      {
        rel: "preload", as: "font", type: "font/woff2",
        href: interLatin, crossOrigin: "anonymous", fetchPriority: "high",
      },
      {
        rel: "preload", as: "font", type: "font/woff2",
        href: spaceGroteskLatin, crossOrigin: "anonymous", fetchPriority: "high",
      },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head><HeadContent /></head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  // Side effects must run in useEffect — placing them in the render body
  // re-registers the `load` listener and re-imports web-vitals on every
  // render, which leaks listeners and wastes a network request per render.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if ("serviceWorker" in navigator) {
      // The offline shell must only ever be installed by the real published
      // site. Registering inside the Lovable editor, an embedded preview, an
      // iframe or dev can serve stale HTML and deleted chunks. In any refused
      // context we also remove a previously installed worker so returning
      // editors recover automatically. `?sw=off` is a manual kill switch.
      const host = window.location.hostname;
      const refused =
        !import.meta.env.PROD ||
        window.self !== window.top ||
        new URLSearchParams(window.location.search).has("sw") ||
        host === "localhost" ||
        host === "127.0.0.1" ||
        host.endsWith(".local") ||
        host.startsWith("id-preview--") ||
        host.startsWith("preview--") ||
        host === "lovableproject.com" ||
        host.endsWith(".lovableproject.com") ||
        host === "lovableproject-dev.com" ||
        host.endsWith(".lovableproject-dev.com") ||
        host === "beta.lovable.dev" ||
        host.endsWith(".beta.lovable.dev") ||
        host.endsWith("-dev.lovable.app");

      if (refused) {
        void navigator.serviceWorker
          .getRegistrations()
          .then((regs) =>
            Promise.allSettled(
              regs
                .filter((r) => (r.active ?? r.waiting ?? r.installing)?.scriptURL.endsWith("/sw.js"))
                .map((r) => r.unregister()),
            ),
          )
          .catch(() => undefined);
      } else {
        const register = () => {
          navigator.serviceWorker.register("/sw.js").catch(() => undefined);
        };
        if (document.readyState === "complete") register();
        else window.addEventListener("load", register, { once: true });
      }
    }
    // Real-user Web Vitals (CLS/LCP/INP/FCP/TTFB) — fire-and-forget,
    // deferred to idle so it never competes with first paint.
    const startVitals = () => {
      void import("@/lib/web-vitals").then((m) => m.startWebVitals()).catch(() => undefined);
    };
    const ric = (window as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    if (ric) ric(startVitals, { timeout: 3000 });
    else setTimeout(startVitals, 1500);

    // Load Google Analytics only after the user grants analytics consent.
    const loadGA = () => {
      const w = window as unknown as { __gaLoaded?: boolean; dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
      if (w.__gaLoaded) return;
      w.__gaLoaded = true;
      const s = document.createElement("script");
      s.async = true;
      s.src = "https://www.googletagmanager.com/gtag/js?id=G-73T9JSBJ8K";
      document.head.appendChild(s);
      w.dataLayer = w.dataLayer || [];
      w.gtag = function (...args: unknown[]) { w.dataLayer!.push(args); };
      w.gtag("js", new Date());
      w.gtag("config", "G-73T9JSBJ8K", { anonymize_ip: true });
    };
    void import("@/lib/cookie-consent").then(({ getStoredConsent, onConsentChange }) => {
      if (getStoredConsent()?.analytics) loadGA();
      return onConsentChange((state) => { if (state.analytics) loadGA(); });
    }).catch(() => undefined);

    // Basemap credential: delivered once at startup from the secret store so it
    // never lives in source, style JSON, or the database.
    void (async () => {
      try {
        const [{ getMapConfig }, { verifyMapTilerKey }] = await Promise.all([
          import("@/lib/map-config.functions"),
          import("@/lib/leaflet-tiles"),
        ]);
        const cfg = await getMapConfig();
        await verifyMapTilerKey(cfg.maptilerKey);
      } catch {
        /* maps fall back to the keyless basemap */
      }
    })();


  }, []);


  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrandingProvider>
          <CookieConsentProvider>
            <Outlet />
          </CookieConsentProvider>
        </BrandingProvider>
        <UpdateAvailableBanner />
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </QueryClientProvider>
  );
}

